import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldOff, ShieldCheck, Crown, Search } from 'lucide-react';
import { api } from '../lib/api';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';

const TABS = [
  { key: 'team', label: 'Admin Team' },
  { key: 'users', label: 'Users' },
  { key: 'notices', label: 'Notices' },
  { key: 'payments', label: 'Payments' },
];

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [notices, setNotices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('team');
  const [userSearch, setUserSearch] = useState('');

  const load = async () => {
    try {
      const [u, n, p] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/notices'),
        api.get('/admin/payments'),
      ]);
      setUsers(u.data.users || []);
      setNotices(n.data.notices || []);
      setPayments(p.data.payments || []);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  };
  useEffect(() => { load(); }, []);

  // PRESERVED user-action logic - exactly as before, do not modify
  const suspend = async (id) => { await api.post(`/admin/users/${id}/suspend`); load(); };
  const promote = async (id) => { await api.post(`/admin/users/${id}/promote`); load(); };

  // Split: admin team vs regular users (purely client-side filter on existing data)
  const adminTeam = useMemo(() => users.filter(u => u.role === 'admin'), [users]);
  const regularUsers = useMemo(() => users.filter(u => u.role !== 'admin'), [users]);

  // Client-side email search on the regular Users tab
  const filteredRegularUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return regularUsers;
    return regularUsers.filter(u => (u.email || '').toLowerCase().includes(q));
  }, [regularUsers, userSearch]);

  const renderUserRow = (u, testIdPrefix) => (
    <div key={u.id} className="tactical-card p-4 flex items-center justify-between" data-testid={`${testIdPrefix}-${u.id}`}>
      <div>
        <div className="font-body text-sm text-white">{u.email}</div>
        <div className="font-body text-[10px] tracking-wide text-white/50 mt-1">
          Role: {u.role} | Sub: {u.subscription_active ? 'Active' : 'Inactive'} | Status: {u.subscription_status || 'n/a'}
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => suspend(u.id)} className="btn-ghost text-[10px]" data-testid={`admin-suspend-${u.id}`}>
          <ShieldOff className="h-3 w-3 inline mr-1" strokeWidth={1.5} /> Suspend
        </button>
        <button onClick={() => promote(u.id)} className="btn-ghost text-[10px]" data-testid={`admin-promote-${u.id}`}>
          <Crown className="h-3 w-3 inline mr-1" strokeWidth={1.5} /> Promote
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-plai-bg flex flex-col" data-testid="admin-page">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard"><Logo size="sm" /></Link>
          <Link to="/dashboard" className="font-body text-xs tracking-[0.2em] uppercase text-white/70 hover:text-white inline-flex items-center gap-1" data-testid="admin-back-link">
            <ArrowLeft className="h-3 w-3" strokeWidth={1.5} /> Dashboard
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-7xl w-full mx-auto px-6 py-12">
        <h1 className="font-headings text-5xl uppercase tracking-tight mb-2">God Mode</h1>
        <p className="font-body text-xs tracking-[0.2em] uppercase text-plai-cta mb-10">Administrative control center</p>

        {err && <div className="border border-red-500/40 bg-red-500/10 p-4 font-body text-sm text-red-300 mb-6" data-testid="admin-error">{err}</div>}

        <div className="flex gap-3 mb-6 flex-wrap">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`font-headings uppercase tracking-wide px-4 py-2 ${tab === t.key ? 'bg-plai-cta text-black' : 'bg-black border border-white/15 text-white hover:border-white/40'}`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'team' && (
          <div className="space-y-2" data-testid="admin-team-list">
            <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">
              {adminTeam.length} admin account{adminTeam.length === 1 ? '' : 's'}
            </p>
            {adminTeam.map(u => renderUserRow(u, 'admin-team-user'))}
            {adminTeam.length === 0 && <p className="font-body text-sm text-white/50" data-testid="admin-team-empty">No admin accounts yet.</p>}
          </div>
        )}

        {tab === 'users' && (
          <div className="space-y-2" data-testid="admin-users-list">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={1.5} />
              <input
                type="search"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                placeholder="Search users by email..."
                className="w-full pl-10 pr-3 py-3 text-sm"
                data-testid="admin-users-search"
                autoComplete="off"
              />
            </div>
            <p className="font-body text-[10px] tracking-[0.2em] uppercase text-white/40 mb-3">
              {filteredRegularUsers.length} of {regularUsers.length} user{regularUsers.length === 1 ? '' : 's'}
            </p>
            {filteredRegularUsers.map(u => renderUserRow(u, 'admin-user'))}
            {regularUsers.length === 0 && <p className="font-body text-sm text-white/50" data-testid="admin-users-empty">No regular users yet.</p>}
            {regularUsers.length > 0 && filteredRegularUsers.length === 0 && (
              <p className="font-body text-sm text-white/50" data-testid="admin-users-no-match">No users match "{userSearch}".</p>
            )}
          </div>
        )}

        {tab === 'notices' && (
          <div className="space-y-2" data-testid="admin-notices-list">
            {notices.map(n => (
              <div key={n.id} className="tactical-card p-4" data-testid={`admin-notice-${n.id}`}>
                <div className="font-body text-sm">{n.jurisdiction} | {n.agency}</div>
                <div className="font-body text-[10px] text-white/50 mt-1">{n.recipient_name} | {new Date(n.created_at).toLocaleString()}</div>
              </div>
            ))}
            {notices.length === 0 && <p className="font-body text-sm text-white/50">No notices yet.</p>}
          </div>
        )}

        {tab === 'payments' && (
          <div className="space-y-2" data-testid="admin-payments-list">
            {payments.map(p => (
              <div key={p.id} className="tactical-card p-4 flex items-center justify-between" data-testid={`admin-payment-${p.id}`}>
                <div>
                  <div className="font-body text-sm">{p.email} - ${p.amount}</div>
                  <div className="font-body text-[10px] text-white/50 mt-1">Status: {p.status} | Payment: {p.payment_status || 'n/a'}</div>
                </div>
                <ShieldCheck className={`h-5 w-5 ${p.payment_status === 'paid' ? 'text-plai-teal' : 'text-white/30'}`} strokeWidth={1.5} />
              </div>
            ))}
            {payments.length === 0 && <p className="font-body text-sm text-white/50">No payments yet.</p>}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
